import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Check, ShieldCheck, Mail, X, Loader2 } from 'lucide-react';
import { BETA_PRICE_HKD } from '@/lib/constants';
import { api } from '@/lib/api';

export default function Beta() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const code = searchParams.get('code');
  const [inviteDetails, setInviteDetails] = useState<{
    email: string;
    plan_type: 'paid_beta' | 'free_beta';
    entitlement_days: number;
  } | null>(null);
  const [isInviteLoading, setIsInviteLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;

    const loadInvite = async () => {
      if (!code) {
        setIsInviteLoading(false);
        return;
      }

      setIsInviteLoading(true);
      setError('');

      try {
        const invite = await api.validateInviteCode(code);
        if (!cancelled) {
          setInviteDetails(invite);
        }
      } catch (err: any) {
        if (!cancelled) {
          setInviteDetails(null);
          if (err.status === 404) {
            setError('Invalid invite code. Please check and try again.');
          } else if (err.status === 410) {
            setError(err.message || 'This invite code is no longer valid.');
          } else {
            setError(err.message || 'Something went wrong. Please try again.');
          }
        }
      } finally {
        if (!cancelled) {
          setIsInviteLoading(false);
        }
      }
    };

    loadInvite();
    return () => {
      cancelled = true;
    };
  }, [code]);

  const handleActivate = async () => {
    if (!code || !inviteDetails || isSubmitting) return;

    setIsSubmitting(true);
    setError('');

    try {
      if (inviteDetails.plan_type === 'free_beta') {
        await api.redeemFreeBeta(code);
        navigate('/welcome-beta?source=free_beta');
        return;
      }

      const { checkout_url } = await api.createCheckoutSession(code);
      window.location.href = checkout_url;
    } catch (err: any) {
      setIsSubmitting(false);
      if (err.status === 409) {
        setError(
          inviteDetails.plan_type === 'free_beta'
            ? 'This free beta invite has already been activated.'
            : 'This invite code has already been used for payment.'
        );
      } else if (err.status === 410) {
        setError('This invite code is no longer valid.');
      } else if (err.status === 400 && err.code === 'invalid_plan_type') {
        setError('This invite code cannot be used for this activation path.');
      } else {
        setError(err.message || 'Something went wrong. Please try again.');
      }
    }
  };

  const handleCancel = () => {
    navigate('/');
  };

  if (!code) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-6">
        <div className="text-center">
          <p className="text-red-500 mb-4">Invalid or missing invite code.</p>
          <button
            onClick={() => navigate('/')}
            className="text-indigo-600 hover:text-indigo-700 font-medium"
          >
            Return to homepage
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-6 relative">
      <button
        onClick={handleCancel}
        disabled={isSubmitting}
        className="absolute top-8 right-8 p-2 text-slate-400 hover:text-slate-600 transition-colors disabled:opacity-50"
      >
        <X className="h-6 w-6" />
      </button>

      <div className="max-w-md w-full text-center mb-12">
        <h1 className="text-3xl font-bold text-slate-900 mb-2">
          {inviteDetails?.plan_type === 'free_beta' ? 'Activate your free beta' : 'Start your paid beta'}
        </h1>
        <p className="text-slate-500">
          {inviteDetails?.email
            ? `You're invited to the Mindstream beta as ${inviteDetails.email}.`
            : "You're invited to the Mindstream beta."}
        </p>
        <div className="mt-4 inline-flex items-center gap-2 px-3 py-1 bg-emerald-50 text-emerald-700 rounded-full text-xs font-bold uppercase tracking-wider">
          Invite Code: {code} ✅
        </div>
      </div>

      <div className="max-w-md w-full bg-white rounded-3xl shadow-xl shadow-slate-200/60 border border-slate-100 overflow-hidden">
        <div className="p-8 border-b border-slate-50">
          {isInviteLoading ? (
            <div className="flex items-center justify-center py-12 text-slate-500">
              <Loader2 className="h-5 w-5 animate-spin mr-2" />
              Validating invite...
            </div>
          ) : inviteDetails ? (
            <>
              <div className="flex justify-between items-start mb-6">
                <div>
                  <h2 className="text-xl font-bold text-slate-900">Mindstream Beta</h2>
                  <p className="text-sm text-slate-500">Complete execution dashboard</p>
                </div>
                <div className="text-right">
                  {inviteDetails.plan_type === 'free_beta' ? (
                    <>
                      <div className="text-2xl font-bold text-slate-900">Free</div>
                      <div className="text-xs text-slate-400 font-medium">
                        {inviteDetails.entitlement_days} days of beta access
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="text-2xl font-bold text-slate-900">HKD${BETA_PRICE_HKD.toLocaleString()}</div>
                      <div className="text-xs text-slate-400 font-medium">
                        for {inviteDetails.entitlement_days} days
                      </div>
                    </>
                  )}
                </div>
              </div>

              <ul className="space-y-4 mb-8">
                {[
                  'Gmail integration (read-only)',
                  'AI action items & follow-ups',
                  'Smart reply drafts',
                  'Cancel anytime',
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-slate-600 text-sm">
                    <Check className="h-5 w-5 text-indigo-500" />
                    {item}
                  </li>
                ))}
              </ul>

              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-100 rounded-xl text-red-600 text-sm text-center">
                  {error}
                </div>
              )}

              <button
                onClick={handleActivate}
                disabled={isSubmitting}
                className="w-full bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-400 text-white font-bold py-4 rounded-2xl shadow-lg shadow-indigo-200 transition-all active:scale-[0.98] flex items-center justify-center gap-2"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin" />
                    {inviteDetails.plan_type === 'free_beta'
                      ? 'Activating beta...'
                      : 'Redirecting to payment...'}
                  </>
                ) : inviteDetails.plan_type === 'free_beta' ? (
                  'Start free beta'
                ) : (
                  'Continue to payment'
                )}
              </button>

              <div className="mt-6 flex flex-wrap justify-center gap-x-4 gap-y-2 text-[10px] text-slate-400 font-medium uppercase tracking-widest">
                <span className="flex items-center gap-1">
                  <ShieldCheck className="h-3 w-3" /> Read-only Gmail
                </span>
                <span className="flex items-center gap-1">
                  <Mail className="h-3 w-3" /> No auto-sending
                </span>
                <span>Cancel anytime</span>
              </div>
            </>
          ) : (
            <div className="text-center py-4">
              <p className="text-red-500 mb-4">{error || 'This invite code could not be validated.'}</p>
              <button
                onClick={() => navigate('/')}
                className="text-indigo-600 hover:text-indigo-700 font-medium"
              >
                Return to homepage
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
